<template>
  <div
    :class="inputClasses"
    class="app-input"
  >
    <label
      v-if="label"
      :class="labelClasses"
      :for="id || name"
    >
      {{ label }}
    </label>

    <!-- Icon -->
    <span
      v-if="$slots.icon"
      class="app-input__icon"
      :class="iconClasses"
    >
      <slot name="icon"/>
    </span>

    <input
      ref="input"
      :id="id || name"
      :type="type"
      :name="name"
      :maxlength="max"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :value="value"
      :class="fieldClasses"
      class="app-input__field"
      @input="handleInput"
      @blur="focused = false"
      @focus="focused = true"
    >

    <div
      v-if="hint"
      class="app-input__hint"
    >
      {{ hint }}
    </div>

    <transition
      name="fade"
      duration="1500ms"
    >
      <div
        v-if="error"
        class="app-input__error"
      >
        <slot
          v-if="$slots.error"
          name="error"
        />
        <small
          v-else
          class="app-input__error-message"
        >
          {{ error }}
        </small>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'app-input',

  props: {
    autocomplete: String,
    id: String,
    label: String,
    value: [String, Number],
    placeholder: String,
    horizontal: Boolean,
    error: String,
    icon: Boolean,
    min: Number,
    max: Number,
    hint: String,
    type: {
      type: String,
      default: 'text'
    },
    name: {
      type: String,
      default: 'app-input__field'
    }
  },

  data () {
    return {
      focused: false,
      blurred: false
    }
  },

  computed: {
    isFloating () {
      return (this.value || this.focused)
      // if (!this.value && !this.focused) return false
      // return 'floating'
    },

    // isDirty () {
    //   if (this.type === )
    // },

    inputClasses () {
      return {
        'active': this.isFloating,
        'horizontal': this.horizontal,
        'focused': this.focused,
        'has-error': this.error,
        'has-label': this.label
      }
    },

    iconClasses () {
      return {
        'floating': this.isFloating,
        'has-label': this.label
      }
    },

    labelClasses () {
      return {
        'has-icon': this.icon,
        'floating': this.isFloating
      }
    },

    fieldClasses () {
      return {
        'has-error': this.error,
        'has-icon': this.icon,
        'has-label': this.label
      }
    }
  },

  methods: {
    handleInput (event) {
      // console.dir(event.target)
      // console.dir(event.target.value)
      this.$emit('input', event.target.value)
    }
  }
}
</script>

<style scoped lang="scss">
$input-color-label: #A7A7A7;
$input-color-background: #F4F4F4;
$input-color-text: #3F3F3F;
$input-color-border: #D6D6D6;
$input-color-divider: #DDD;
$input-color-border-active: #3CBBF5;
$input-color-error: #DF2C21;

.app-input {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  min-width: 150px;
  width: 100%;
  margin-bottom: 20px;

  @include breakpoint('tablet-portrait-up') {
    margin-bottom: 40px;
  }

  @include breakpoint('tablet-landscape-up') {
    margin-left: 10px;

    &:first-child {
      margin-left: 0;
    }
  }

  label {
    position: absolute;
    top: 0;
    left: 15px;
    font-size: 16px;
    font-weight: 400;
    color: $input-color-label;
    pointer-events: none;
    transform-origin: 0 0;
    transform: translate3D(0px, 15px, 0);
    transition: transform 175ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      left: 30px;
      font-size: 22px;
      transform: translate3D(0px, 26px, 0);
    }

    &.floating {
      transform: translate3D(1px, 5px, 0) scale(0.75);
      @include breakpoint('tablet-portrait-up') {
        transform: translate3D(2px, 12px, 0) scale(0.63);
      }
    }

    &.has-icon {
      transform: translate3D(20px, 15px, 0);
      @include breakpoint('tablet-portrait-up') {
        transform: translate3D(36px, 26px, 0);
      }
    }

    &.has-icon.floating {
      transform: translate3D(1px, 5px, 0) scale(0.75);
      @include breakpoint('tablet-portrait-up') {
        transform: translate3D(2px, 12px, 0) scale(0.63);
      }
    }
  }

  &__field {
    width: 100%;
    height: 55px;
    color: $input-color-text;
    background: $input-color-background;
    box-shadow: none;
    border: 1px solid transparent;
    border-top: 1px solid $input-color-border;
    // flex-basis: 100%;
    outline: none;
    padding: 10px 0 10px 15px;
    font-size: 18px;
    caret-color: $brand-color-primary;
    border-radius: 4px;
    transition: outline 150ms ease-out,
      box-shadow 150ms ease-out,
      border-color 150ms ease-out;
    -webkit-appearance: none;
    -moz-appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @include breakpoint('tablet-portrait-up') {
      border-width: 2px;
      font-size: 22px;
      height: 75px;
      padding: 10px 0 10px 30px;
    }

    &.has-icon {
      padding-left: 30px;

      @include breakpoint('tablet-portrait-up') {
        padding-left: 50px;
      }
    }

    &:focus {
      border-color: $input-color-border-active;
    }

    &.has-label {
      padding-top: 25px;
    }

    &.has-error {
      border-color: $input-color-error;
      box-shadow: 0 4px 0 0 $input-color-error;
    }
  }

  &__error {
    position: absolute;
    bottom: -23px;
    right: 0;

    @include breakpoint('tablet-portrait-up') {
      bottom: auto;
      right: auto;
      top: -28px;
      left: 0;
    }
  }

  &__error-message {
    color: $input-color-error;
    font-size: 12px;
    font-style: italic;

    @include breakpoint('tablet-portrait-up') {
      font-size: 14px;
    }
  }

  &__hint {
    position: absolute;
    bottom: -25px;
    left: 0;
    font-size: 9px;
    color: #090909;
    opacity: 0.5;

    @include breakpoint('tablet-portrait-up') {
      font-size: 16px;
    }

    @include breakpoint('desktop-up') {
      font-size: 14px;
    }
  }

  &__icon {
    position: absolute;
    left: 15px;
    font-size: 16px;
    top: 16px;

    @include breakpoint('tablet-portrait-up') {
      font-size: 22px;
      top: 26px;
      left: 30px;
    }

    &.floating.has-label {
      top: 22px;

      @include breakpoint('tablet-portrait-up') {
        top: 32px;
      }
    }
  }
}

::placeholder {
  color: $text-color-placeholder;
}

.horizontal.app-input {
  display: flex;
  flex-direction: row;
  border: 1px solid transparent;
  border-top: 1px solid $input-color-border;
  box-shadow: none;
  border-radius: 4px;
  background: $input-color-background;
  transition: box-shadow 150ms ease-out,
    border-color 150ms ease-out;

  @include breakpoint('tablet-portrait-up') {
    border-width: 2px;
  }

  label {
    position: relative;
    left: 0;
    transform: none;
    display: block;
    flex-basis: 50%;
    width: 50%;
    max-width: 50%;
    order: 2;
    pointer-events: all;
    color: $input-color-text;
    background-color: transparent;
    box-shadow: none;
    border: 1px solid transparent;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:before {
    position: absolute;
    right: 50%;
    top: 12px;
    content: '';
    display: block;
    height: 30px;
    width: 1px;
    background-color: $input-color-divider;

    @include breakpoint('tablet-portrait-up') {
      top: 16px;
      height: 45px;
    }
  }

  &.focused {
    border-color: $input-color-border-active;
  }

  &.has-error {
    border-color: $input-color-error;
    box-shadow: 0 4px 0 0 $input-color-error;
  }
}

.horizontal .app-input {
  &__field {
    width: 50%;
    max-width: 50%;
    display: block;
    flex-basis: 50%;
    padding: 10px 0;
    text-align: center;
    background-color: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
  }
}
</style>
